import {
  Box,
  Button,
  CssBaseline,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";

import CategoryIcon from "@mui/icons-material/Category";
import ImageIcon from "@mui/icons-material/Image";
import QuizIcon from "@mui/icons-material/Quiz";
import MuiDrawer from "@mui/material/Drawer";
import { styled } from "@mui/material/styles";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { APP_ICONS, APP_LOGO } from "../../assets";
import { setUserData } from "../../redux/slices/Login";
import { ADMIN_ROUTES } from "../../routes";

const drawerWidth = 250;

const openedMixin = (theme) => ({
  width: drawerWidth,
  borderRadius: "12px",
  boxShadow: "0px 0px 20px 0px #003B951F",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  borderRadius: "12px",
  boxShadow: "0px 0px 20px 0px #003B951F",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(1, 1),

  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

const useStyle = {
  activeTab: {
    bgcolor: "primary.main",
    color: "#ffffff !important",
    mx: 1,
    my: 0.5,
    borderRadius: 2,
    "&:hover": {
      bgcolor: "primary.main",
    },
    "& .MuiListItemText-primary": {
      color: "secondary.light",
    },
  },
  inActiveTab: {
    margin: 0.5,
    borderRadius: 2,

    "&:hover": {},
    "& .MuiListItemText-primary": {},
  },
};

export default function NavigationDrawer(props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(true);
  const { pathname } = useLocation();

  const handleToggleDrawer = () => {
    setOpen((prev) => !prev);
  };

  const handleNavigation = (path) => {
    if (open) {
      setOpen(true);
      navigate(path);
    } else {
      navigate(path);
      setOpen(false);
    }
  };

  const handleLogout = () => {
    navigate("/login");
    localStorage.removeItem("user");
    dispatch(setUserData(null));
  };
  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <Stack direction={"row"} gap={1} my={1}>
            <img
              src={APP_LOGO.circularLogo}
              style={{
                width: 44,
                height: 44,
              }}
            />
            {open && (
              <Typography
                color={"primary.main"}
                fontSize={"1.625rem"}
                fontWeight={600}
              >
                BB Life
              </Typography>
            )}
          </Stack>
        </DrawerHeader>

        <img
          src={open ? APP_ICONS.chevronLeftIcon : APP_ICONS.chevronRightIcon}
          width={30}
          height={30}
          onClick={handleToggleDrawer}
          style={{
            position: "fixed",
            top: "70px",
            left: open ? `${drawerWidth - 20}px` : "50px",
            zIndex: 1300,
            transition: "left 0.3s",
            cursor: "pointer",
          }}
        />

        <List sx={{ mt: 3 }}>
          {ADMIN_ROUTES.map((route, index) => (
            <>
              {open && (
                <Typography
                  mx={3}
                  fontSize={"0.75rem"}
                  color="customColor.dustyGray"
                  fontWeight={600}
                  my={(index === 4 || index === 6 || index === 8) && 1}
                  display={
                    index === 4 || index === 6 || index === 8 ? "block" : "none"
                  }
                >
                  {index === 4
                    ? "USERS MANAGEMENT SYSTEM"
                    : index === 6
                    ? "BB-LIFE E-WALLET"
                    : index === 8
                    ? "SPONSORED"
                    : null}
                </Typography>
              )}

              <ListItem key={index} disablePadding sx={{ display: "block" }}>
                <ListItemButton
                  onClick={() => handleNavigation(route.path)}
                  className="active-tab"
                  sx={[
                    {
                      minHeight: 48,
                      px: 2.5,
                      ...(route.path === pathname
                        ? useStyle.activeTab
                        : useStyle.inActiveTab),
                    },
                    open
                      ? {
                          justifyContent: "initial",
                        }
                      : {
                          justifyContent: "center",
                        },
                  ]}
                >
                  <ListItemIcon
                    sx={[
                      {
                        minWidth: 0,
                        justifyContent: "center",
                      },
                      open
                        ? {
                            mr: 2,
                          }
                        : {
                            mr: "auto",
                          },
                    ]}
                  >
                    {/* {index % 2 === 0 ? <InboxIcon /> : <MailIcon />} */}
                    {route.path == "/faq" ? (
                      <QuizIcon
                        style={{
                          color: route.path == pathname ? "#FFF" : "#4D4D4D",
                        }}
                      />
                    ) : route.path == "/category" ? (
                      <CategoryIcon
                        style={{
                          color: route.path == pathname ? "#FFF" : "#4D4D4D",
                        }}
                      />
                    ) : route.path == "/banners" ? (
                      <ImageIcon
                        style={{
                          color: route.path == pathname ? "#FFF" : "#4D4D4D",
                        }}
                      />
                    ) : (
                      <img
                        src={
                          route.path === pathname
                            ? route.activeIcon
                            : route.inActiveIcon
                        }
                        width={24}
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={route.name}
                    sx={{
                      ...(open
                        ? {
                            opacity: 1,
                          }
                        : {
                            opacity: 0,
                          }),
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </>
          ))}
        </List>
        {open ? (
          <Button
            variant="contained"
            onClick={handleLogout}
            sx={{ height: 45, width: 232, m: "auto" }}
            endIcon={<img src={APP_ICONS.logoutIconWhite} width={20} />}
          >
            Logout
          </Button>
        ) : (
          <IconButton
            sx={{
              backgroundColor: "primary.main",
              mx: 1,
              mt: 4,
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "primary.main",
              },
            }}
          >
            <img src={APP_ICONS.logoutIconWhite} width={20} />
          </IconButton>
        )}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, overflow: "auto" }}>
        {props.children}
      </Box>
    </Box>
  );
}
